import * as z from "zod/v4";
import { returnsAnalyticsDataQualitySchema } from "./returns-analytics-shared";

export const getReturnsChurnBreakdownInputSchema = z.object({
  startDate: z.string().min(1).optional(),
  endDate: z.string().min(1).optional(),
});
export type GetReturnsChurnBreakdownInput = z.infer<
  typeof getReturnsChurnBreakdownInputSchema
>;

/**
 * Rich cohort row — per segment (deal size bucket / owner / stage at
 * churn), answers "how many churned, how long were they on for, why
 * did they leave, and how much revenue did we lose?"
 *
 * Durations are medians (p50) — robust to outlier tenure values which
 * a simple average distorts. $ lost uses realized lifetime revenue
 * because that's what was actually collected.
 */
const churnCohortRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  churnCount: z.number().int().min(0),
  /** Median days from first_revenue_at to churned_at. */
  medianDaysOnPlatform: z.number().min(0).nullable(),
  /** Median days from completed_at to churned_at (post-onboarding lifetime). */
  medianDaysPostOnboarding: z.number().min(0).nullable(),
  /** Top churn reason for the segment. From linked deal's Churn & Winbacks stage. */
  topChurnReason: z.string().nullable(),
  /** Number of teams in this cohort whose reason matches topChurnReason. */
  topChurnReasonCount: z.number().int().min(0),
  /** Summed realized_revenue_lifetime_cents for churned teams in this cohort. */
  revenueLostCents: z.string(),
  /** Teams in cohort that churned but are currently revenue_generating again. */
  reactivatedCount: z.number().int().min(0),
});
export type ChurnCohortRow = z.infer<typeof churnCohortRowSchema>;

/**
 * Backwards-compat simple bucket row (count only). Kept for the
 * time-to-churn histogram which doesn't need the enrichment.
 */
const timeBucketRowSchema = z.object({
  bucket: z.enum(["<30d", "30-60d", "60-90d", "90-180d", ">180d"]),
  churnCount: z.number().int().min(0),
});
export type ChurnTimeBucketRow = z.infer<typeof timeBucketRowSchema>;

/**
 * Self-serve vs assisted comparison — the primary product question.
 * "Do teams that completed self-serve retain better than those who
 * needed help?" Three cohorts: completed self-serve, help requested,
 * no onboarding link.
 */
const selfServeComparisonRowSchema = z.object({
  cohort: z.enum(["completed", "helpRequested", "notLinked"]),
  /** Total teams in this cohort that had any active revenue in the window. */
  activeTeams: z.number().int().min(0),
  churnCount: z.number().int().min(0),
  /** churnCount / activeTeams * 100. Null if activeTeams = 0. */
  churnRatePct: z.number().min(0).max(100).nullable(),
  medianDaysOnPlatform: z.number().min(0).nullable(),
  revenueLostCents: z.string(),
});
export type SelfServeChurnRow = z.infer<typeof selfServeComparisonRowSchema>;

export const getReturnsChurnBreakdownOutputSchema = z.object({
  totalChurned: z.number().int().min(0),
  reactivatedCount: z.number().int().min(0),
  reactivationRatePct: z.number().min(0).max(100).nullable(),
  totalRevenueLostCents: z.string(),
  selfServeComparison: z.array(selfServeComparisonRowSchema),
  byDealSize: z.array(churnCohortRowSchema),
  byOwner: z.array(churnCohortRowSchema),
  byStageAtChurn: z.array(churnCohortRowSchema),
  timeToChurn: z.array(timeBucketRowSchema),
  dataQuality: returnsAnalyticsDataQualitySchema,
});
export type GetReturnsChurnBreakdownOutput = z.infer<
  typeof getReturnsChurnBreakdownOutputSchema
>;

export const getReturnsChurnBreakdown = {
  name: "getReturnsChurnBreakdown" as const,
  input: getReturnsChurnBreakdownInputSchema,
  output: getReturnsChurnBreakdownOutputSchema,
};
